class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

function paintCartDrawerFromSection(parsedState) {
  const cartDrawerSection = parsedState.sections?.['cart-drawer'];
  if (!cartDrawerSection) return;

  const cartDrawerWrapper = document.querySelector('cart-drawer');
  const drawer = document.getElementById('CartDrawer');
  const sectionDoc = new DOMParser().parseFromString(cartDrawerSection, 'text/html');
  const sourceDrawer = sectionDoc.querySelector('#CartDrawer');

  if (drawer && sourceDrawer) {
    drawer.innerHTML = sourceDrawer.innerHTML;
    cartDrawerWrapper?.rebindDrawerElements?.();
  }

  if (cartDrawerWrapper) {
    cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    const drawerItems = cartDrawerWrapper.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.toggle('is-empty', parsedState.item_count === 0);
  }

  const bubbleSection = parsedState.sections?.['cart-icon-bubble'];
  const bubble = document.getElementById('cart-icon-bubble');
  if (bubble && bubbleSection) {
    const bubbleContent = new DOMParser()
      .parseFromString(bubbleSection, 'text/html')
      .querySelector('.shopify-section');
    if (bubbleContent) bubble.innerHTML = bubbleContent.innerHTML;
  }
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      /* Avoid partial DOM replace when cart-drawer HTML was already returned: renderContents paints
         #CartDrawer; onCartUpdate only swaps cart-drawer-items + footer and breaks empty-drawer layout. */
      if (event.cartData?.sections && typeof event.cartData.sections['cart-drawer'] === 'string') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const sourceDrawer = html.querySelector('#CartDrawer');
          const targetDrawer = document.getElementById('CartDrawer');
          const isEmpty = !html.querySelector('.cart-item');

          if (targetDrawer && sourceDrawer) {
            if (isEmpty) {
              targetDrawer.innerHTML = sourceDrawer.innerHTML;
              cartDrawerWrapper?.classList.add('is-empty');
              cartDrawerWrapper?.rebindDrawerElements?.();
              this.classList.add('is-empty');
            } else {
              const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
              for (const selector of selectors) {
                const targetElement = document.querySelector(selector);
                const sourceElement = html.querySelector(selector);
                if (targetElement && sourceElement) {
                  targetElement.replaceWith(sourceElement);
                }
              }
              cartDrawerWrapper?.classList.remove('is-empty');
              this.classList.remove('is-empty');
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);
    const removingItem = parseInt(quantity, 10) === 0;

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        const shouldClearDiscount =
          removingItem &&
          window.ContrabandDiscount?.shouldClearDiscountAfterRemove?.(line, parsedState);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          const cartDrawerSection = parsedState.sections?.['cart-drawer'];
          const isDrawerItems = this.tagName === 'CART-DRAWER-ITEMS';

          // CHANGED: always repaint from cart/change — skipping paint when clearing discount left stale is-empty / empty drawer HTML while items remained
          if (isDrawerItems && cartDrawerSection) {
            paintCartDrawerFromSection(parsedState);
          } else {
            this.getSectionsToRender().forEach((section) => {
              const elementToReplace =
                document.getElementById(section.id).querySelector(section.selector) ||
                document.getElementById(section.id);
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            });
          }
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        const finishUpdate = (cartData) => {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: cartData || parsedState,
            variantId: variantId,
          });
        };

        if (shouldClearDiscount && window.ContrabandDiscount?.handleLineItemRemoved) {
          window.ContrabandDiscount
            // CHANGED: force discount clear after paint — DOM line is gone so shouldClearDiscountAfterRemove would skip the reset
            .handleLineItemRemoved(line, parsedState, { force: true })
            .then((cart) => {
              if (cart) {
                this.classList.toggle('is-empty', cart.item_count === 0);
                if (cartFooter) cartFooter.classList.toggle('is-empty', cart.item_count === 0);
                if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', cart.item_count === 0);
              }
              finishUpdate(cart);
            })
            .catch(() => finishUpdate());
        } else {
          finishUpdate();
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

/*
  CartAddButton
  ----------------------------------------------------------------------------
  Handles "add to cart" clicks for elements marked with the [data-cart-add]
  attribute (e.g. the fullscreen product-slider quick-add buttons).

  Replaces the old standalone fetch('/cart/add.js') + manual
  fetch('/?sections=cart-drawer,cart-icon-bubble') pattern with a single
  request that asks Shopify to render the cart-drawer sections itself, then
  hands the result straight to <cart-drawer>.renderContents(), which is the
  same code path Dawn's own product-form.js uses. This removes the need for
  a second network round-trip and a hand-rolled section-refresh function.
*/
if (!customElements.get('cart-add-button')) {
  customElements.define(
    'cart-add-button',
    class CartAddButton extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.syncState(event.cartData);
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      syncState(cartData) {
        const button = this.querySelector('button');
        if (!button || !button.classList.contains('added')) return;

        const variantId = button.dataset.variantId;
        const items = cartData?.items || [];
        const stillInCart = items.some((item) => String(item.variant_id ?? item.id) === String(variantId));

        if (!stillInCart) {
          button.disabled = false;
          button.textContent = button.dataset.idleLabel || 'Add';
          //button.classList.remove('added');
        }
      }

      onClick(event) {
        event.preventDefault();

        // The clickable <button> lives inside <cart-add-button>; data-variant-id
        // and the label text are on that inner button, not on this wrapper element.
        const button = event.target.closest('button') || this.querySelector('button');
        if (!button || button.disabled) return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        const cartDrawer = document.querySelector('cart-drawer');
        const idleLabel = button.dataset.idleLabel || button.textContent;
        const addedLabel = button.dataset.addedLabel || 'Added ✓';

        // Persist the original label so syncState() can restore it later, even
        // though by then button.textContent has already been overwritten.
        button.dataset.idleLabel = idleLabel;

        button.disabled = true;
        button.textContent = button.dataset.loadingLabel || 'Adding...';

        const config = {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [],
            sections_url: window.location.pathname,
          }),
        };

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              // Shopify returns { status, message, description } on failure (e.g. sold out, quantity rules)
              const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
              if (errors) errors.textContent = parsedState.description || parsedState.message;
              button.disabled = false;
              button.textContent = idleLabel;
              return;
            }

            button.textContent = addedLabel;
            button.classList.add('added');

            if (cartDrawer) cartDrawer.renderContents(parsedState);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-add-button',
              productVariantId: variantId,
              cartData: parsedState,
            });
          })
          .catch((error) => {
            console.error(error);
            button.disabled = false;
            button.textContent = idleLabel;
          });
      }
    }
  );
}